import { Fragment, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Box, useMediaQuery, useTheme } from "@mui/material";
import {
    KeyboardDoubleArrowDown as KeyboardDoubleArrowDownIcon,
    KeyboardDoubleArrowUp as KeyboardDoubleArrowUpIcon,
    Add as AddIcon,
    Delete as DeleteIcon
} from "@mui/icons-material";

import { useMSALPayload } from '@initia-digital/auth-client-utils';
import {
    BasicLoader,
    FilledButton, IconButton, PrestartHazardListItem,
    Typography, useSnackbar
} from "@initia-digital/component-library"
import { baseDefinedAndNotNullNarrowing, S3 } from '@initia-digital/ts-utils';
import { isCriticalOrSiteSpecificHazard } from '@initia-digital/data-service-mapping';
import { FileUploadStatus, HazardObservationMediaType, ICriticalOrSiteSpecificHazard, IHazardMedia, IPrestartHazard } from '@initia-digital/types/work-and-safety';

import { SectionHeader } from "./SectionHeader"
import { useWorkAndSafetyStore } from "../store";
import { DEFAULT_APP_RESIZE_OPTIONS, DEFAULT_WEB_RESIZE_OPTIONS, loadFileUrl, scaleImage } from '../utils/files';
import { putS3Object } from '../api/objectStorage';
import { IPrestartStageProps } from '../interfaces/componentInterfaces';


export const HazardObservations = ({ handleSave }: IPrestartStageProps) => {
    // ------------------------------------
    const [ hazardIdWithFileLoading, setHazardIdWithFileLoading ] = useState<string | null>(null);

    const {
        editPrestartState, updateEditPrestartState
    } = useWorkAndSafetyStore();
    const {
        newHazardsAndControls,
        hazardObservationMedia
    } = editPrestartState;

    const params = useParams();
    const { jobId, planId, prestartId } = params;

    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    const { queueSnackbar } = useSnackbar();

    // AUTH AND DATA INTEGRATION
    // ---------------------
    const authDetail = useMSALPayload();

    const hazardObservationMediaGroupsByHazardId = useMemo(() => {
        // ---------------------
        if (!baseDefinedAndNotNullNarrowing(editPrestartState)) return {} as Record<string, IHazardMedia[]>;
        return Object.groupBy(hazardObservationMedia, (hazardObservationMedia) => hazardObservationMedia?.hazardId || 'noHazardId');
    }, [editPrestartState.hazardObservationMedia]);

    const loadFileUrlCb = useCallback((
        image: { filename: string } & IHazardMedia
    ) => loadFileUrl(image, authDetail, queueSnackbar), [authDetail, queueSnackbar]);

    const handleHazardObservationMediaUpload = useCallback(async (
        hazard: IPrestartHazard | ICriticalOrSiteSpecificHazard,
        files: File[]
    ) => {
        // ---------------------
        const newHazardObservationMediaList: IHazardMedia[] = [];
        for (const file of files) {
            // -----------------------------------------
            setHazardIdWithFileLoading(hazard.id);
            try {
                // ------------------------------
                const fileId = uuidv4();
                const path = `${import.meta.env.VITE_ENV_LOWER}-work-and-safety/${jobId}/prestart/hazard-observation/${hazard.id}/hazard-observation-media-${fileId}/original/${S3.toSafePath(file.name)}`;
                const appPath = `${import.meta.env.VITE_ENV_LOWER}-work-and-safety/${jobId}/prestart/hazard-observation/${hazard.id}/hazard-observation-media-${fileId}/app/${S3.toSafePath(file.name)}`;
                const webPath = `${import.meta.env.VITE_ENV_LOWER}-work-and-safety/${jobId}/prestart/hazard-observation/${hazard.id}/hazard-observation-media-${fileId}/web/${S3.toSafePath(file.name)}`;

                let hazardMediaType: HazardObservationMediaType = 'NEW_OBSERVATION';
                if (isCriticalOrSiteSpecificHazard(hazard)) {
                    // ------------------------------
                    hazardMediaType = 'HIGHLIGHTED';
                } 

                const newHazardObservationMedia = {
                    id: fileId,
                    type: hazardMediaType,
                    hazardId: hazard.id ?? '',
                    hazardName: hazard.hazardName ?? '',
                    jobId: (jobId as string),
                    planId: (planId as string),
                    prestartId: '',
                    path: path,
                    name: file.name,
                    notes: ``,
                    size: file.size,
                    fileType: file.type,
                    file,
                    uploadStatus: "PENDING" as FileUploadStatus,
                    includeInAppendix: false
                };         
                
                // UPLOAD ORIGINAL TO S3
                await putS3Object(authDetail, {
                    file,
                    path
                });

                // Add file to data state
                newHazardObservationMediaList.push(newHazardObservationMedia);

                // HANDLE RESIZED IMAGE OPTIONS TOO
                const appFile = await scaleImage(file, DEFAULT_APP_RESIZE_OPTIONS);
                await putS3Object(authDetail, {
                    file: appFile,
                    path: appPath
                });
                const webFile = await scaleImage(file, DEFAULT_WEB_RESIZE_OPTIONS);
                await putS3Object(authDetail, {
                    file: webFile,
                    path: webPath
                });
            } catch (err) {
                // ------------------------------
                console.error(err);
                queueSnackbar(`There was an error uploading ${file.name}`, 'error', 'Error', 3000, '1rem');
            }
        }
        setHazardIdWithFileLoading(null);
        updateEditPrestartState({
            ...editPrestartState,
            hazardObservationMedia: [
                ...editPrestartState.hazardObservationMedia,
                ...newHazardObservationMediaList
            ]
        });
        // Save the plan with file uploads
        // - This has been added initially to match with aniticipated user expectations
        await handleSave({
            ...editPrestartState,
            hazardObservationMedia: [...editPrestartState.hazardObservationMedia, ...newHazardObservationMediaList]
        });
    }, [editPrestartState, handleSave, jobId, planId, prestartId, queueSnackbar]);

    const handleHazardObservationMediaDelete = useCallback(async (
        file: IHazardMedia
    ) => {
        // ---------------------
        updateEditPrestartState({
            ...editPrestartState,
            hazardObservationMedia: editPrestartState.hazardObservationMedia.filter(media => media.id !== file.id)
        });
    }, [editPrestartState]);

    return (
        <Box
            id="hazard-observations"
            display="flex" flexDirection="column"
            border={isSmallerThanSm ? "none" : "1px solid lightgrey"}
            borderTop={`1px solid ${theme.palette.secondary.main}`} borderRadius={isSmallerThanSm ? "none" : "5px"}
            pb="20px" px={isSmallerThanSm ? '10px' : '20px'}
            mt={isSmallerThanSm ? '20px' : '0px'} pt={isSmallerThanSm ? '20px' : '0px'}
        >
            <Box display="flex" flexDirection="row" alignItems="center">
                <SectionHeader label="3. New or changed hazards" />
                <Box display="flex" flexDirection="row" justifyContent="flex-end">
                    <Box mr="10px">
                        <IconButton
                            size="vsmall"
                            onClick={() => window.location.hash = '#working-with-others'}
                            tooltipTitle={`Go to Working with Others`}
                            color={theme.palette.secondary.contrastText}
                            backgroundColor={theme.palette.secondary.main}
                            hoverColor={theme.palette.primary.main}
                            hoverBackgroundColor={theme.palette.primary.contrastText}
                        >
                            <KeyboardDoubleArrowDownIcon />
                        </IconButton>
                    </Box>
                    <Box mr="10px">
                        <IconButton
                            size="vsmall"
                            onClick={() => window.location.hash = '#review-the-plans'}
                            tooltipTitle={`Go to Review the Plans`}
                            color={theme.palette.secondary.contrastText}
                            backgroundColor={theme.palette.secondary.main}
                            hoverColor={theme.palette.primary.main}
                            hoverBackgroundColor={theme.palette.primary.contrastText}
                        >
                            <KeyboardDoubleArrowUpIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
            <Box
                display="flex" flexDirection="row" gap={1} alignItems="center"
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`New hazards observed on site ${newHazardsAndControls.length > 0 ? `(${newHazardsAndControls.length})` : ``}`}
                </Typography>
            </Box>
            <Box
                lineHeight={0.8}
            >
                <Typography
                    fontSize="0.75rem"
                    fontWeight={300}
                    color={theme.palette.primary.contrastText}
                >
                    Note any new or changed hazards you can see. Record controls that will ensure you can start work safely.
                </Typography>
            </Box>
            <Box
            >
                <Box>
                    <Box display="flex" flexDirection="row" my={1} pl={0.5}>
                        <FilledButton
                            startIcon={<AddIcon />}
                            onClick={() => {
                                // ---------------------
                                updateEditPrestartState({
                                    ...editPrestartState,
                                    newHazardsAndControls: [
                                        ...newHazardsAndControls,
                                        {
                                            id: uuidv4(),
                                            type: 'NEW_HAZARD',
                                            hazardName: '',
                                            description: '',
                                            hazardControls: [],
                                            jobId: jobId ?? '',
                                            planId: planId ?? '',
                                            prestartId: prestartId ?? '',
                                            tags: []
                                        }
                                    ]
                                });
                            }}
                        >
                            New
                        </FilledButton>
                    </Box>
                </Box>
            </Box>
            {
                newHazardsAndControls.length === 0 &&
                <Box display="flex" flexDirection="row" justifyContent="center">
                    <Typography
                        fontSize={"0.9rem"}
                        fontWeight={300}
                        color={theme.palette.primary.contrastText}
                    >
                        No new on site hazard observations recorded 
                    </Typography>
                </Box>
            }
            {
                newHazardsAndControls.length > 0 &&
                <Box
                    display="flex" flexDirection="column" rowGap={1}
                >
                    {
                        newHazardsAndControls.map(hazardObservation => {
                            // --------------------------
                            return (
                                <Fragment key={hazardObservation.id}>
                                <PrestartHazardListItem
                                    hazard={hazardObservation}
                                    actionButtons={[
                                        <IconButton
                                            size="small"
                                            tooltipTitle="Delete hazard"
                                            onClick={() => {
                                                // ---------------------
                                                updateEditPrestartState({
                                                    ...editPrestartState,
                                                    newHazardsAndControls: editPrestartState.newHazardsAndControls
                                                        .filter(hazard => hazard.id !== hazardObservation.id)
                                                });
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    ]}
                                    handleHazardChange={(updatedHazard) => {
                                        // ---------------------
                                        updateEditPrestartState({
                                            ...editPrestartState,
                                            newHazardsAndControls: editPrestartState.newHazardsAndControls.map(hazard =>
                                                hazard.id === updatedHazard.id ? updatedHazard : hazard
                                            )
                                        });
                                    }}
                                    images={baseDefinedAndNotNullNarrowing(hazardObservation?.id) ? (hazardObservationMediaGroupsByHazardId[hazardObservation.id] ?? []).map(media => ({ ...media, filename: media.name })) : []}
                                    loadImage={loadFileUrlCb}
                                    handleImageUpload={async (files) => {
                                        await handleHazardObservationMediaUpload(hazardObservation, files);
                                    }}
                                    handleImageDelete={async (file) => {
                                        await handleHazardObservationMediaDelete(file);
                                    }}
                                    showGalleryWhenCollapsed={true}
                                    maxExpandedImageHeight={150}
                                    maxCollapsedImageHeight={100}
                                />
                                {
                                    hazardIdWithFileLoading === hazardObservation.id && (
                                        <BasicLoader
                                            label="Loading"
                                            size={80}
                                        />
                                    )
                                }
                                </Fragment>
                            )
                        })
                    }
                </Box>
            }
        </Box>
    )
}